import { useMemo } from "react";
import { useMLSCompliance } from "./useMLSCompliance";
import { Property } from "../../types/search";

/**
 * Custom hook to fetch MLS logos for a list of properties
 * Returns a map of MLS board codes to their respective logos
 */
export const useMLSLogos = (properties: Property[]) => {
  // Get unique MLS board codes from properties
  const visibleMLSCodes = useMemo(() => {
    const codes = new Set<string>();
    properties.forEach((property) => {
      if (property.mlsBoardCode) {
        codes.add(property.mlsBoardCode);
      }
    });
    return Array.from(codes);
  }, [properties]);

  // Fetch MLS compliance data for all visible MLS boards
  const mlsComplianceQueries = useMLSCompliance(visibleMLSCodes);

  // Create a map of MLS board codes to logos for quick lookup
  const mlsLogosMap = useMemo(() => {
    const map = new Map<string, string>();
    mlsComplianceQueries.forEach((query) => {
      if (query.isSuccess && query.data?.status === "success") {
        map.set(query.data.data.mlsBoardCode, query.data.data.mlsLogo);
      }
    });
    return map;
  }, [mlsComplianceQueries]);

  return {
    mlsLogosMap,
    mlsComplianceQueries,
    isLoading: mlsComplianceQueries.some((query) => query.isLoading),
  };
};
